import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, StageChip, Stat } from "../../dashboard/primitives";
import {
  setMilestoneStatus,
  subscribeAllProjects,
  subscribeClients,
  subscribeMilestones,
} from "../../projects/api";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { api } from "../../lib/api";
import { ensureStripeCustomer } from "../../lib/stripeCustomer";
import { SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const STATUSES = ["pending", "invoiced", "paid"];

const chipFor = (s) =>
  s === "paid" ? "paid" : s === "invoiced" ? "awaiting_approval" : "pending";

const stripeChipFor = (s) => {
  if (s === "paid") return "paid";
  if (s === "open" || s === "draft") return "awaiting_approval";
  if (s === "void" || s === "uncollectible") return "closed";
  return "in_progress";
};

const InvoicesSection = () => {
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [clients, setClients] = useState([]);
  const [rowsByProject, setRowsByProject] = useState({});
  const [filter, setFilter] = useState("open");
  const [busyId, setBusyId] = useState(null);
  const [errorById, setErrorById] = useState({});

  useEffect(
    () =>
      subscribeAllProjects((v) => {
        setProjects(v);
        setProjectsLoaded(true);
      }),
    []
  );
  useEffect(() => subscribeClients(setClients), []);

  const showSkeleton = useMinLoading(!projectsLoaded);

  const usersByUid = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients]
  );

  // Live-subscribe to milestones for every active project so webhook writes
  // (stripeInvoiceStatus, hostedInvoiceUrl, invoicePdf, paidAt, …) stream in.
  useEffect(() => {
    const active = projects.filter((p) => !p.archived);
    const unsubs = active.map((p) =>
      subscribeMilestones(p.id, (ms) =>
        setRowsByProject((prev) => ({ ...prev, [p.id]: ms }))
      )
    );
    return () => unsubs.forEach((u) => u && u());
  }, [projects]);

  const flat = useMemo(() => {
    const out = [];
    projects.forEach((p) => {
      if (p.archived) return;
      const ms = rowsByProject[p.id] || [];
      ms.forEach((m) => out.push({ ...m, project: p, owner: usersByUid[p.ownerUid] }));
    });
    return out;
  }, [projects, rowsByProject, usersByUid]);

  const visible = useMemo(() => {
    if (filter === "all") return flat;
    if (filter === "paid") return flat.filter((r) => r.status === "paid");
    return flat.filter((r) => r.status !== "paid");
  }, [flat, filter]);

  const totals = useMemo(() => {
    const paid = flat.filter((r) => r.status === "paid").reduce((n, r) => n + (r.amount || 0), 0);
    const invoiced = flat.filter((r) => r.status === "invoiced").reduce((n, r) => n + (r.amount || 0), 0);
    const pending = flat.filter((r) => r.status === "pending").reduce((n, r) => n + (r.amount || 0), 0);
    return { paid, invoiced, pending, outstanding: invoiced + pending };
  }, [flat]);

  const handleStatus = async (row, status) => {
    await setMilestoneStatus(row.project.id, row.id, status);
  };

  const sendInvoice = async (row) => {
    const rowKey = `${row.project.id}-${row.id}`;
    setBusyId(rowKey);
    setErrorById((prev) => ({ ...prev, [rowKey]: null }));
    try {
      const customerId = await ensureStripeCustomer({
        user: { uid: row.project.ownerUid, email: row.owner?.email },
        profile: row.owner,
      });
      // Backend createInvoiceOrder: adds items (amount in DOLLARS — it multiplies
      // by 100 internally), creates + finalizes, and mirrors the finalized invoice
      // to stockist/{customerId}/invoices/. It does not write back to our milestone
      // doc, so we do that ourselves from the response.
      const { data: invoice } = await api.post("/create-invoice-order", {
        customerId,
        userId: row.project.ownerUid,
        memo: `${row.project.title} · ${row.label}`,
        items: [
          {
            amount: Number(row.amount),
            description: `${row.project.title} · ${row.label}`,
            quantity: 1,
          },
        ],
      });

      await updateDoc(doc(db, "projects", row.project.id, "milestones", row.id), {
        stripeInvoiceId: invoice.id,
        stripeInvoiceStatus: invoice.status,
        hostedInvoiceUrl: invoice.hosted_invoice_url || null,
        invoicePdf: invoice.invoice_pdf || null,
        amountDueCents: invoice.amount_due ?? null,
        amountPaidCents: invoice.amount_paid ?? null,
        dueDate: invoice.due_date ? new Date(invoice.due_date * 1000) : null,
        finalizedAt: serverTimestamp(),
        status: "invoiced",
      });
    } catch (err) {
      setErrorById((prev) => ({
        ...prev,
        [rowKey]: err?.response?.data?.error || err.message || "Failed to send invoice",
      }));
    } finally {
      setBusyId(null);
    }
  };

  const voidInvoice = async (row) => {
    if (!window.confirm("Void this Stripe invoice? The client will no longer be able to pay it.")) return;
    const rowKey = `${row.project.id}-${row.id}`;
    setBusyId(rowKey);
    try {
      await api.post("/void-invoice", { invoiceId: row.stripeInvoiceId });
      await updateDoc(doc(db, "projects", row.project.id, "milestones", row.id), {
        stripeInvoiceStatus: "void",
        status: "pending",
      });
    } catch (err) {
      setErrorById((prev) => ({
        ...prev,
        [rowKey]: err?.response?.data?.error || err.message || "Failed to void",
      }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="fw-stat-grid">
        <Stat label="Paid" value={fmt(totals.paid)} loading={showSkeleton} />
        <Stat label="Invoiced" value={fmt(totals.invoiced)} loading={showSkeleton} />
        <Stat label="Pending" value={fmt(totals.pending)} loading={showSkeleton} />
        <Stat label="Outstanding" value={fmt(totals.outstanding)} loading={showSkeleton} />
      </div>

      <Card
        title="Invoices"
        action={
          <div style={{ display: "flex", gap: 6 }}>
            {["open", "paid", "all"].map((f) => (
              <button
                key={f}
                className={`fw-btn fw-btn--ghost ${filter === f ? "is-active" : ""}`}
                style={{ padding: "6px 10px", opacity: filter === f ? 1 : 0.7 }}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        }
      >
        {showSkeleton ? (
          <SkeletonList count={5} ariaLabel="Loading invoices">
            <SkeletonRow cols={5} widths={["1.4fr", "1fr", "90px", "120px", "180px"]} />
          </SkeletonList>
        ) : visible.length === 0 ? (
          <Empty>No invoices in this view</Empty>
        ) : (
          <div className="fw-invoice-table">
            <div className="fw-invoice-table__head">
              <span>Project / client</span>
              <span>Milestone</span>
              <span style={{ textAlign: "right" }}>Amount</span>
              <span>Status</span>
              <span>Action</span>
            </div>
            {visible.map((r) => {
              const rowKey = `${r.project.id}-${r.id}`;
              const busy = busyId === rowKey;
              const err = errorById[rowKey];
              const hasInvoice = !!r.stripeInvoiceId;
              return (
                <div key={rowKey} className="fw-invoice-row">
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{r.project.title}</div>
                    <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                      {r.owner?.displayName || r.owner?.email || r.project.ownerUid?.slice(0, 8)}
                    </div>
                  </div>
                  <div>
                    <div>{r.label}</div>
                    <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                      #{String((r.order ?? 0) + 1).padStart(2, "0")} · {Math.round((r.pct || 0) * 100)}%
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontFamily: "var(--fw-mono)" }}>{fmt(r.amount)}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <StageChip status={chipFor(r.status)} />
                    {hasInvoice && r.stripeInvoiceStatus && (
                      <StageChip status={stripeChipFor(r.stripeInvoiceStatus)} />
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    {!hasInvoice && r.status !== "paid" && (
                      <button
                        className="fw-btn fw-btn--primary"
                        style={{ padding: "6px 10px" }}
                        onClick={() => sendInvoice(r)}
                        disabled={busy}
                      >
                        {busy ? "Sending…" : "Send invoice"}
                      </button>
                    )}
                    {hasInvoice && (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {r.hostedInvoiceUrl && (
                          <a
                            className="fw-btn fw-btn--ghost"
                            style={{ padding: "4px 8px", fontSize: 10 }}
                            href={r.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        )}
                        {r.invoicePdf && (
                          <a
                            className="fw-btn fw-btn--ghost"
                            style={{ padding: "4px 8px", fontSize: 10 }}
                            href={r.invoicePdf}
                            target="_blank"
                            rel="noreferrer"
                          >
                            PDF
                          </a>
                        )}
                        {r.stripeInvoiceStatus !== "paid" && r.stripeInvoiceStatus !== "void" && (
                          <button
                            className="fw-btn fw-btn--ghost"
                            style={{ padding: "4px 8px", fontSize: 10, color: "#c0392b" }}
                            onClick={() => voidInvoice(r)}
                            disabled={busy}
                          >
                            Void
                          </button>
                        )}
                      </div>
                    )}
                    <select
                      className="fw-select"
                      style={{ width: "auto", fontSize: 11, padding: "3px 6px" }}
                      value={r.status}
                      onChange={(e) => handleStatus(r, e.target.value)}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {err && (
                      <div style={{ color: "#c0392b", fontSize: 10, fontFamily: "var(--fw-mono)" }}>{err}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default InvoicesSection;
